<script setup lang="ts">
import { ref, toRefs, watch, computed, useTemplateRef } from '#imports';
import { useElementSize, useTimeoutFn } from '@vueuse/core';

import { useOpenClosePopper } from './usePopper';

import { type PopperStrategyType, UiPopperPlacement } from './PopperPlacement';
import { ClickAwayMode, useClickAway, useClickAwayListener } from '../../utils/useClickAway';

const props = withDefaults(defineProps<{
  placement?: UiPopperPlacement;
  enableClickAway?: boolean;
  offsetDistance?: number;
  offsetBorder?: number;
  hover?: boolean;
  disabled?: boolean;
  openDelay?: number;
  closeDelay?: number;
  zIndex?: number;
  arrow?: boolean;
  content?: string;
  interactive?: boolean;
  defaultOpenHandler?: boolean;
  minWidth?: string;
  strategy?: PopperStrategyType;
  auto?: boolean;
}>(), {
  placement: UiPopperPlacement.Bottom,
  enableClickAway: false,
  offsetDistance: 4,
  offsetBorder: 16,
  hover: false,
  disabled: false,
  openDelay: 0,
  closeDelay: 0,
  zIndex: 3000,
  arrow: false,
  content: '',
  interactive: true,
  defaultOpenHandler: true,
  minWidth: '',
  auto: false,
});

const emit = defineEmits(['open:popper', 'close:popper']);

const popperComponentNode = useTemplateRef('popperComponentNode');
const reference = useTemplateRef('reference');
const floating = useTemplateRef('floating');
const floatingArrow = useTemplateRef('floatingArrow');
const { closeDelay, enableClickAway, disabled, openDelay, placement, arrow, strategy, auto } = toRefs(props);
const { isOpen, open, close, style, middlewareData } = useOpenClosePopper(reference, floating, {
  emit,
  strategy: strategy.value,
  offset: props.offsetDistance,
  offsetBorder: props.offsetBorder,
  placement,
  auto: auto.value,
  arrow: arrow.value && floatingArrow,
});
const shouldShowPopper = computed(() => isOpen.value && !disabled.value);
const { width } = useElementSize(reference);
const popperWidth = computed(() => {
  if (props.minWidth) return props.minWidth;
  if (width.value) {
    return `${width.value}px`;
  }
  return '100px';
});

let cancelOpen: null = null;
let cancelClose: null = null;

function openPopper() {
  if (disabled.value || cancelOpen) {
    return;
  }
  if (cancelClose) {
    cancelClose();
    cancelClose = null;
  }
  const { stop } = useTimeoutFn(() => {
    cancelOpen = null;
    open();
  }, openDelay.value);
  cancelOpen = stop;
}

function closePopper() {
  if (cancelOpen) {
    cancelOpen();
    cancelOpen = null;
  }
  const { stop } = useTimeoutFn(() => {
    cancelClose = null;
    close();
  }, closeDelay.value);
  cancelClose = stop;
}

function togglePopper() {
  if (isOpen.value) closePopper();
  else openPopper();
}

if (enableClickAway.value) {
  useClickAwayListener(popperComponentNode, closePopper, enableClickAway, ClickAwayMode.External);
}

const { clickAwayEvent } = useClickAway(popperComponentNode, closePopper, ref(true), ClickAwayMode.Focusout);

function focusOut(event: Event) {
  if (!enableClickAway.value) {
    clickAwayEvent(event);
  }
}

watch(disabled, (disabled) => {
  if (isOpen.value && disabled) {
    close();
  }
});

const side = computed(() => placement.value.split('-')[0]);
const staticSide = computed(
  () =>
    ({
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[side.value] || 'bottom'),
);

const arrowPlacement = computed(() => {
  const arrow = middlewareData.value.arrow || { x: 0, y: 0 };
  const { offsetWidth } = floatingArrow.value || {};
  if (['bottom', 'top'].includes(staticSide.value)) {
    return {
      left: `${arrow.x || 0}px`,
      [staticSide.value]: `${-(offsetWidth || 0) / 2}px`,
    };
  }
  return {
    top: `${arrow.y || 0}px`,
    [staticSide.value]: `${-(offsetWidth || 0) / 2}px`,
  };
});

defineSlots<{
  opener?(props: { open: () => void; close: () => void; isOpen: boolean; toggle: () => void; }): void;
  default?(props: { close: () => void; isOpen: boolean; }): void;
}>();

defineExpose({
  openPopper,
  closePopper,
})
</script>

<template lang="pug">
.ui-popper(
  ref="popperComponentNode"
  @focusout="focusOut"
  @mouseleave="hover && closePopper()"
)
  .popper-trigger(
    ref="reference"
    @mouseover="hover && openPopper()"
    @click="defaultOpenHandler ? togglePopper() : null"
    @focus="openPopper"
    @keyup.esc="closePopper"
  )
    slot(name="opener" :open="openPopper" :close="close" :toggle="togglePopper" :isOpen="isOpen")
  Transition(name="fade")
    .popper(
      v-show="shouldShowPopper"
      ref="floating"
      :style="{...style, 'z-index': zIndex, 'min-width': popperWidth}"
      @mouseover="hover && openPopper()"
      @click="!interactive && closePopper()"
    )
      slot(:close="close" :isOpen="isOpen") {{ content }}
      .arrow(v-if="arrow" ref="floatingArrow" :style="arrowPlacement")
</template>

<style lang="scss" scoped>

.arrow {
  transition: background 250ms ease-in-out;
  position: absolute;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  background: var(--popper-theme-background-color);
  transform: rotate(45deg);
}

.ui-popper {
  display: flex;
}

.popper-trigger {
  width: 100%;
  display: flex;
}

.popper {
  transition: background 250ms ease-in-out;
  background: var(--popper-theme-background-color);
  --uno: br-8;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
